import { useFocusEffect } from "@react-navigation/native";
import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../redux/reducers";
import { PaymentSourceType } from "../enums";
import { DomusoApiService } from "../api/DomusoApiService";
import { getDate } from "date-fns";
import moment from "moment";

interface FirstValidSourceParams {
  certifiedDateRange?: { certified_start_day: number; certified_end_day: number };
  allowedPayments?: "ANY" | "DO_NOT_ACCEPT_PAYMENT" | "CASH_EQUIVALENT";
  omitBA?: boolean;
  // An id you want to use regardless of COF
  preSelectedPaymentSourceId?: any;
  keepInactive?: boolean;
  allowCC?: boolean;
  allowACH?: boolean;
  updated?: boolean;
}
export const usePaymentMethodInformation = (
  returnFirstValidSourceParams?: FirstValidSourceParams
) => {
  const {
    preSelectedPaymentSourceId,
    keepInactive = true,
    omitBA,
    updated,
  } = returnFirstValidSourceParams || {};
  const dispatch = useDispatch();
  const cognitoUser = useSelector((state: RootState) => state.auth.cognitoUser);
  const [paymentSources, setPaymentSources] = useState<any>(null);
  const [firstValidSource, setFirstValidSource] = useState<any>(null);
  const [error, setError] = useState<any>(null);
  const [isLoading, setLoading] = useState<boolean>();
  const service = new DomusoApiService(cognitoUser, dispatch);
  const allowCC = returnFirstValidSourceParams?.allowCC;
  const allowACH = returnFirstValidSourceParams?.allowACH;
  const layoutEffect = React.useCallback(() => {
    setLoading(true);
    service
      .getPaymentSources()
      .then((resp: any) => {
        if (!Array.isArray(resp)) {
          setPaymentSources([]);
        }
        const sources = resp.filter((source: any) => {
          if (omitBA && source.type !== PaymentSourceType.CREDIT_CARD) return false;
          if (!allowCC && source.type == PaymentSourceType.CREDIT_CARD) return false;
          if (!allowACH && source.type == PaymentSourceType.BANK_ACCOUNT) return false;
          // The isPreselected value is what will keep the temporary payment method showing in the list for the scenario that the user didn't selected the "Store for future use" option.
          // Once payment is completed isPreselected should be resetted for the payment method to be excluded from list since isPreselected and allow_cof will be false.
          const isPreselected = Number(preSelectedPaymentSourceId) === Number(source.id);
          const shouldKeepInactivePaymentMethod = keepInactive || source.active;
          const isCreatedFromCheckscan = source.created_from_check_scan;
          return (
            !isCreatedFromCheckscan &&
            shouldKeepInactivePaymentMethod &&
            (isPreselected || !!source.allow_cof)
          );
        });
        // Sort the payment methods active first
        sources.sort((a: any, b: any) => {
          if (a.active && !b.active) return -1;
        });
        setPaymentSources(sources);
        if (returnFirstValidSourceParams && Array.isArray(sources) && sources.length) {
          const firstSource = sources[0];
          // TODO: When certified payment methods come in we'll need the contents of returnFirstValidSourceParams
          setFirstValidSource({ ...firstSource, paymentType: firstSource.type });
        }
      })
      .finally(() => setLoading(false))
      .catch((e) => setError(e));
  }, [preSelectedPaymentSourceId, updated]);
  useFocusEffect(layoutEffect);
  return { paymentSources, firstValidSource, isLoading, error };
};

export const isWithinCertifiedDateRange = ({
  certified_start_day,
  certified_end_day,
  currDate = getDate(new Date()),
}: Record<string, number>) => {
  return certified_start_day <= currDate && currDate <= certified_end_day;
};

export const isWithinCertifiedDate = ({
  certified_start_day,
  certified_end_day,
  currDate = getDate(new Date()),
}: Record<string, number>) => {
  if (certified_start_day > certified_end_day) {
    if (certified_start_day <= currDate && currDate <= 31) {
      return true;
    } else if (certified_end_day >= currDate) {
      return true;
    } else {
      return false;
    }
  } else {
    return certified_start_day <= currDate && currDate <= certified_end_day;
  }
};

export const getDateRange = ({
  certified_start_day,
  certified_end_day,
  currDate,
}: Record<string, number>) => {
  if (certified_start_day > certified_end_day) {
    if (
      !isWithinCertifiedDate({
        certified_start_day,
        certified_end_day,
        currDate: getDate(currDate),
      })
    ) {
      var today = new Date(currDate);
      var lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();

      let differenceStartDay = certified_start_day - new Date(currDate).getDate();
      let differenceEndDay = lastDayOfMonth - new Date(currDate).getDate() + certified_end_day;

      if (certified_start_day > lastDayOfMonth) {
        let extraDays = certified_start_day - lastDayOfMonth;
        differenceStartDay = differenceStartDay - extraDays + 1;
      }

      let firstAvailableDueDate = moment(currDate)
        .add(differenceStartDay, "days")
        .format("MM/DD/YYYY");

      let lastAvailableDueDate = moment(currDate)
        .add(differenceEndDay, "days")
        .format("MM/DD/YYYY");
      return {
        firstAvailableDueDate: firstAvailableDueDate,
        lastAvailableDueDate: lastAvailableDueDate,
      };
    }
  } else {
    if (
      !isWithinCertifiedDate({
        certified_start_day,
        certified_end_day,
        currDate: getDate(currDate),
      })
    ) {
      var today = new Date(currDate);
      var lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();

      let differenceStartDay = certified_start_day - new Date(currDate).getDate();

      let differenceEndDay =
        certified_end_day < lastDayOfMonth
          ? certified_end_day - new Date(currDate).getDate()
          : lastDayOfMonth - new Date(currDate).getDate();

      if (differenceStartDay < 0) {
        differenceStartDay = lastDayOfMonth - new Date(currDate).getDate() + certified_start_day;
      }
      if (differenceEndDay <= 0) {
        differenceEndDay = lastDayOfMonth - new Date(currDate).getDate() + certified_end_day;
      }
      let firstAvailableDueDate = moment(currDate)
        .add(differenceStartDay, "days")
        .format("MM/DD/YYYY");
      let lastAvailableDueDate = moment(currDate)
        .add(differenceEndDay, "days")
        .format("MM/DD/YYYY");
      var today = new Date(currDate);
      var lastDayOfNextMonth = new Date(today.getFullYear(), today.getMonth() + 2, 0).getDate();

      if (differenceEndDay > lastDayOfNextMonth) {
        let extraDays = differenceEndDay - lastDayOfNextMonth;
        lastAvailableDueDate = moment(lastAvailableDueDate)
          .subtract(extraDays, "days")
          .format("MM/DD/YYYY");
      }
      return {
        firstAvailableDueDate: firstAvailableDueDate,
        lastAvailableDueDate: lastAvailableDueDate,
      };
    }
  }
};

export const shouldDisablePayment = ({ isCertifiedMethod, allowedPayments }: any) => {
  if (allowedPayments === "ANY") {
    return false;
  }
  if (allowedPayments === "DO_NOT_ACCEPT_PAYMENT") {
    return true;
  }
  if (!isCertifiedMethod && allowedPayments === "CASH_EQUIVALENT") {
    return true;
  }
  return false;
};
